import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '@/app/providers/authContext';
import { Icon } from '@/components/ui';
import { backofficeAreasForProfile } from '@/features/backoffice/sections';
import {
  THEME_LABELS,
  THEME_PREFERENCES,
  useThemePreference,
  type ThemePreference,
} from '@/lib/theme';

import { PROFILE_LABELS } from '../navigation';
import styles from '../store/StoreFooter.module.css';

/** What each internal profile does, as the Figma's "Você está operando como" card says it. */
const PROFILE_DUTY: Partial<Record<string, string>> = {
  commercial_backoffice:
    'Prepara catálogos, monta e publica tabelas de preço e acompanha pedidos e entregas. Não vê custo de aquisição.',
  procurement: 'Cadastra produtos, fornecedores e unidades e registra o custo de aquisição.',
  controllership: 'Aprova tabelas acima do limite, concilia pagamentos e acompanha os boletos.',
  platform_admin: 'Cadastra clientes e acessos e enxerga todas as áreas da plataforma.',
};

/**
 * The internal footer (Figma BC1): the profile's areas as the site map, who the session operates
 * as, the LGPD line and the base. Only real pages are linked; the Figma's internal support
 * contact, access policy and legal documents do not exist yet (listed in the PR).
 */
export function InternalFooter() {
  const { profile } = useAuth();
  const areas = useMemo(() => backofficeAreasForProfile(profile), [profile]);
  const [theme, setTheme] = useThemePreference();
  const [lifting, setLifting] = useState(0);

  return (
    <footer className={styles.footer}>
      <button
        type="button"
        className={styles.top}
        data-lift={lifting === 0 ? undefined : lifting}
        onClick={() => {
          setLifting((value) => (value === 1 ? 2 : 1));
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      >
        <Icon name="chevronUp" size={16} className={styles.topIcon} />
        Voltar ao topo
      </button>
      <div className={styles.columns}>
        {areas.slice(0, 4).map((area) => (
          <div key={area.label} className={styles.column}>
            <p className={styles.columnTitle}>{area.label}</p>
            <ul className={styles.links}>
              {area.sections.map((section) => (
                <li key={section.to}>
                  <Link to={section.to} className={styles.link}>
                    {section.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
        {profile !== null ? (
          <div className={styles.buyingFor}>
            <p className={styles.eyebrow}>Você está operando como</p>
            <p className={styles.buyingUnit}>{PROFILE_LABELS[profile]} · Instoque</p>
            {PROFILE_DUTY[profile] !== undefined ? (
              <p className={styles.buyingCompany}>{PROFILE_DUTY[profile]}</p>
            ) : null}
          </div>
        ) : null}
      </div>
      <div className={styles.base}>
        <p className={styles.copy}>
          <span className={styles.mark} aria-hidden="true">
            In
          </span>
          © {new Date().getFullYear()} Instoque · backoffice de compras de food service com a
          Sapore
        </p>
        <label className={styles.theme}>
          <span className="visually-hidden">Aparência</span>
          <select
            className={styles.themeSelect}
            value={theme}
            onChange={(event) => {
              setTheme(event.target.value as ThemePreference);
            }}
          >
            {THEME_PREFERENCES.map((option) => (
              <option key={option} value={option}>
                Aparência: {THEME_LABELS[option]}
              </option>
            ))}
          </select>
          <Icon name="chevronDown" size={14} className={styles.themeChevron} />
        </label>
        <p className={styles.version}>
          versão <code>{import.meta.env.VITE_APP_VERSION}</code>
        </p>
      </div>
    </footer>
  );
}
